// app_client/src/pages/admin/UserManagement.jsx
import React, { useEffect, useState } from 'react';
import useUserStore from '../../store/userStore';
import { UserPlus, Edit, Power, Shield, Mail, KeyRound, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import UserModal from '../../components/modals/UserModal';
import PasswordModal from '../../components/modals/PasswordModal';
import { useConfirmation } from '../../components/providers/ConfirmationProvider';

const UserManagement = () => {
  const { confirm } = useConfirmation();
  const { users, roles, fetchUsers, fetchRoles, saveUser, deleteUser, loading, updateUserStatus } = useUserStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isPassModalOpen, setIsPassModalOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers, fetchRoles]);

  const handleEdit = (user) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleSave = async (formData, userId) => {
    const result = await saveUser(formData, userId);
    if (result.success) {
      setIsModalOpen(false);
    } else {
      alert(result.message);
    }
  };

  const handlePasswordSave = async (newPassword, userId) => {
    const result = await saveUser({ password: newPassword }, userId);
    if (result.success) {
      setIsPassModalOpen(false);
    } else {
      alert("Şifre güncellenemedi!");
    }
  };

  const handleDelete = async (user) => {
    if (await confirm(`${user.name} kullanıcısını silmek istediğinize emin misiniz?`)) {
      const result = await deleteUser(user._id);
      if (result.success) {
        toast.success("Kullanıcı silindi");
      } else {
        toast.error(result.message || "Kullanıcı silinemedi", { duration: 6000 });
      }
    }
  };

  if (loading) return <div className="p-8 text-center font-medium">Kullanıcılar hazırlanıyor...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-black text-slate-800 tracking-tight">Kullanıcı Yönetimi</h1>
          <p className="text-slate-500 text-sm">Sistemdeki personelleri ve yetkilerini yönetin.</p>
        </div>
        <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-lg shadow-blue-500/20"
          onClick={handleAdd}
        >
          <UserPlus size={20} />
          Yeni Kullanıcı Ekle
        </button>
      </div>

      {/* Kullanıcı Kartları / Tablo */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50/50 border-b border-slate-100">
              <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-widest">Kullanıcı</th>
              <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-widest">Rol</th>
              <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-widest">Durum</th>
              <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-widest text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {users.map((u) => (
              <tr key={u._id} className="hover:bg-slate-50/50 transition-colors group">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                      {u.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-bold text-slate-700">{u.name}</div>
                      <div className="text-xs text-slate-400 flex items-center gap-1">
                        <Mail size={12} /> {u.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-600 px-3 py-1 rounded-lg text-xs font-bold uppercase">
                    <Shield size={14} />
                    {u.role?.name || 'Tanımsız'}
                  </span>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                    u.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${u.status === 'Active' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    {u.status === 'Active' ? 'Aktif' : 'Pasif'}
                  </span>
                </td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors" title="Düzenle"
                      onClick={() => handleEdit(u)}
                    >
                      <Edit size={18} />
                    </button>
                    <button 
                      onClick={() => { setSelectedUser(u); setIsPassModalOpen(true); }}
                      className="p-2 hover:bg-amber-50 text-amber-600 rounded-lg"
                      title="Şifre Değiştir"
                    >
                      <KeyRound size={18} />
                    </button>
                    <button 
                      onClick={() => updateUserStatus(u._id, u.status === 'Active' ? 'Passive' : 'Active')}
                      className={`p-2 rounded-lg transition-colors ${u.status === 'Active' ? 'hover:bg-red-50 text-red-500' : 'hover:bg-emerald-50 text-emerald-500'}`}
                      title={u.status === 'Active' ? 'Durdur' : 'Aktifleştir'}
                    >
                      <Power size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(u)}
                      className="p-2 hover:bg-rose-50 text-rose-500 rounded-lg transition-colors"
                      title="Sil"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isModalOpen && (
        <UserModal 
          key={selectedUser?._id || 'new'} 
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSave}
          user={selectedUser}
          roles={roles}
        />
      )}
      {isPassModalOpen && (
        <PasswordModal 
          key={`pass-${selectedUser?._id}`}
          isOpen={isPassModalOpen}
          onClose={() => setIsPassModalOpen(false)}
          onSave={handlePasswordSave}
          user={selectedUser}
        />
      )}
    </div>
  );
};

export default UserManagement;
